import { useCallback, useState } from "react";
import { AuditLog, ResourceType } from "@common/types/auditLog";
import { SortOrder } from "@common/constants";
import { useGetAuditLogsQuery } from "@api/auditLog.api";

const ACTIVITY_PAGE_SIZE = 50;

export type AccrualActivityResourceType =
    | ResourceType.BILL
    | ResourceType.INVOICE;

export function getAccrualEntityLabel(
    resourceType: AccrualActivityResourceType,
): "bill" | "invoice" {
    return resourceType === ResourceType.BILL ? "bill" : "invoice";
}

export interface AccrualActivityState {
    logs: AuditLog[];
    totalCount: number;
    hasMore: boolean;
    nextPageSize: number;
    isPending: boolean;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;
    refetch: () => void;
    loadMore: () => void;
}

/**
 * Owns the audit-log page for one bill/invoice. `limit` is keyed by resource so
 * that reusing the same component instance for another document (the details
 * asides are not remounted when the selection changes) starts from the first
 * page instead of replaying the previous document's accumulated page size.
 */
export function useAccrualActivity({
    resourceType,
    resourceId,
    isEnabled,
}: {
    resourceType: AccrualActivityResourceType;
    resourceId: string;
    isEnabled: boolean;
}): AccrualActivityState {
    const resourceKey = `${resourceType}:${resourceId}`;
    const [pagination, setPagination] = useState({
        resourceKey,
        limit: ACTIVITY_PAGE_SIZE,
    });
    const limit =
        pagination.resourceKey === resourceKey
            ? pagination.limit
            : ACTIVITY_PAGE_SIZE;

    const { data, isPending, isFetching, isSuccess, isError, error, refetch } =
        useGetAuditLogsQuery({
            filters: { resourceType, resourceId },
            page: 1,
            limit,
            sort: { field: "createdAt", order: SortOrder.DESC },
            options: { enabled: isEnabled },
        });

    const loadMore = useCallback(
        () =>
            setPagination({
                resourceKey,
                limit: limit + ACTIVITY_PAGE_SIZE,
            }),
        [resourceKey, limit],
    );

    const handleRefetch = useCallback(() => {
        void refetch();
    }, [refetch]);

    const logs: AuditLog[] = data?.data ?? [];
    const totalCount = data?.total ?? 0;

    return {
        logs,
        totalCount,
        hasMore: totalCount > logs.length,
        nextPageSize: Math.min(ACTIVITY_PAGE_SIZE, totalCount - logs.length),
        isPending,
        isFetching,
        isSuccess,
        isError,
        errorMessage:
            error instanceof Error
                ? `Could not load activity: ${error.message}`
                : "Could not load activity.",
        refetch: handleRefetch,
        loadMore,
    };
}
